import { ThermostatAdjustment } from '../../../../types/thermostatAdjustment'
import { toDegrees } from '../../../../utils/string'
import dayjs from 'dayjs'
import { match } from 'ts-pattern'
import { getAdjustmentType } from '../utils'
import { useState } from 'react'
import { thermostatComfortSettingsCollection } from '../../../../reducers/thermostatComfortSettings'
import { ID } from '../../../../types/model'
import useUtilityPrograms from '@/app/hooks/useUtilityPrograms'
import { getCostCategoryTypeFromRange } from '../../../pages/thermostat/cost-categories/utils/costCategoryMappings'

interface Props {
  adjustment: ThermostatAdjustment | undefined | null
  thermostatId: ID
}

function useLastAdjustmentCardHeaderViewModel({
  adjustment,
  thermostatId,
}: Props) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const adjustmentType = getAdjustmentType(adjustment)
  const shouldShow = !!adjustment && adjustmentType !== 'other'

  // Fetch thermostat comfort settings for deltas
  const { data: comfortSettings } =
    thermostatComfortSettingsCollection.useFetch(
      `/thermostats/${thermostatId}/comfort_settings/`,
      { require: adjustmentType === 'demand_response' },
    )

  // Fetch enrolled programs
  const { enrolledDevicePrograms } = useUtilityPrograms({
    onlyMultiPrograms: true,
  })

  // Find the enrolled program for this thermostat
  const enrolledProgram = enrolledDevicePrograms?.find((program) =>
    program.enrollment_overview?.thermostat_enrollments?.some(
      (enrollment) => enrollment.thermostat_id === thermostatId,
    ),
  )

  // Get cost category information from the adjustment itself
  const costCategoryControl = adjustment?.cost_category_control

  const handleOpenModal = () => {
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
  }

  if (!shouldShow) {
    return {
      shouldShow: false,
      title: '',
      subtitle: '',
      adjustedTemperature: null,
      formattedTime: '',
      adjustmentTypeTitle: '',
      isModalOpen: false,
      handleOpenModal,
      handleCloseModal,
      modalContent: null,
    }
  }

  const adjustedTemperature =
    adjustment.cool_setpoint || adjustment.heat_setpoint
  const formattedTime = dayjs(adjustment.created).format('h:mm A on MM/DD/YYYY')

  const adjustmentTypeTitle = match(adjustmentType)
    .with('save_money', () => 'Save Money')
    .with('pre-cooling', () => 'Pre-cooling')
    .with('demand_response', () => 'DR Event')
    .exhaustive()

  const title = `${adjustmentTypeTitle} adjusted temp to ${toDegrees(
    adjustedTemperature,
  )}`
  const subtitle = `At ${formattedTime}`

  // Modal content based on adjustment type with real data and URLs
  type ReasonPart = { text: string; bold?: boolean }
  type ReasonLine = ReasonPart[]

  const modalContent = match(adjustmentType)
    .with('save_money', () => {
      if (!costCategoryControl) {
        return {
          title:
            'We adjusted your thermostat to save you money & help the grid!',
          reasons: [
            [{ text: 'Save Money is active' }],
            [{ text: 'Cost category information unavailable' }],
            [{ text: 'Temperature adjustment applied' }],
          ] as ReasonLine[],
          adjustSettingsUrl: `/devices/thermostat/${thermostatId}/cost-categories`,
        }
      }

      const costCategoryType = getCostCategoryTypeFromRange({
        min_cost_cents: costCategoryControl.min_cost_cents,
        max_cost_cents: costCategoryControl.max_cost_cents,
      })

      const categoryLabel = match(costCategoryType)
        .with('negative', () => 'Negative')
        .with('low', () => 'Low')
        .with('medium', () => 'Medium')
        .with('high', () => 'High')
        .exhaustive()

      const costDisplay = match(costCategoryType)
        .with('negative', () => '<0¢ per kWh')
        .with('low', () => {
          const maxCents = costCategoryControl.max_cost_cents || 0
          return `0-${maxCents}¢ per kWh`
        })
        .with('medium', () => {
          const minCents = costCategoryControl.min_cost_cents || 0
          const maxCents = costCategoryControl.max_cost_cents || 0
          return `${minCents}-${maxCents}¢ per kWh`
        })
        .with('high', () => {
          const minCents = costCategoryControl.min_cost_cents || 0
          return `>${minCents}¢ per kWh`
        })
        .exhaustive()

      const deltaText = costCategoryControl.cool_setpoint_delta
        ? `${costCategoryControl.cool_setpoint_delta > 0 ? '+' : ''}${
            costCategoryControl.cool_setpoint_delta
          }°`
        : '-2°'

      return {
        title: 'We adjusted your thermostat to save you money & help the grid!',
        reasons: [
          [{ text: 'Save Money is active' }],
          [{ text: `${categoryLabel} cost hour (${costDisplay})` }],
          [
            { text: `${categoryLabel} cost shift is set to ` },
            { text: deltaText, bold: true },
          ],
        ] as ReasonLine[],
        adjustSettingsUrl: `/devices/thermostat/${thermostatId}/cost-categories`,
      }
    })
    .with('pre-cooling', () => ({
      title: 'We adjusted your thermostat to save you money & help the grid!',
      reasons: [
        [{ text: 'Pre-cooling is active' }],
        [
          {
            text: 'We cooled your home in advance to avoid high energy use later',
          },
        ],
      ] as ReasonLine[],
      adjustSettingsUrl: `/devices/thermostat/${thermostatId}/precool-preheat-settings`,
    }))
    .with('demand_response', () => {
      const programName = enrolledProgram?.name || '{program}'
      console.dir(enrolledProgram)
      const firstComfortSetting = comfortSettings?.[0]
      const heatDelta = firstComfortSetting?.event_heat_setpoint_delta || 3
      const coolDelta = firstComfortSetting?.event_cool_setpoint_delta || 3

      return {
        title: 'We adjusted your thermostat to save you money & help the grid!',
        reasons: [
          [{ text: 'Demand response event in progress' }],
          [
            {
              text: `Your settings are set to shift `,
            },
            {
              text: `-${heatDelta} for heat and +${coolDelta} for AC`,
              bold: true,
            },
          ],
          [{ text: `You're enrolled in ${programName}` }],
        ] as ReasonLine[],
        adjustSettingsUrl: `/devices/thermostat/${thermostatId}/grid-support`,
      }
    })
    .exhaustive()

  return {
    shouldShow: true,
    title,
    subtitle,
    adjustedTemperature,
    formattedTime,
    adjustmentTypeTitle,
    isModalOpen,
    handleOpenModal,
    handleCloseModal,
    modalContent,
  }
}

export default useLastAdjustmentCardHeaderViewModel
